import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Clock } from "lucide-react";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;

function timezones() {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "Europe/Paris", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Australia/Sydney", "Asia/Kolkata", "Asia/Tokyo"];
  }
}

export function WeeklyLetterCard({ cfg, onChange }) {
  const [letter, setLetter] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const zones = useMemo(timezones, []);

  useEffect(() => {
    api.get("/letter/preview").then(({ data }) => setLetter(data)).catch(() => {});
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/letter/send-now");
      toast.success(`This week's letter is on its way to ${data.sent_to}.`);
      setLetter((l) => (l ? { ...l, last_sent_at: new Date().toISOString() } : l));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const enabled = cfg.weekly_letter_enabled;

  return (
    <div className="mt-6 card-paper p-6 sm:p-8" data-testid="settings-letter-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#E5ECE6] flex items-center justify-center text-[#5F7463]">
            <Mail size={18} />
          </div>
          <div>
            <h2 className="font-serif-display text-xl text-[#2C221E]">Weekly letter</h2>
            <p className="text-sm text-[#73635B] mt-1 max-w-sm">
              A short email once a week with the shape of your days and one gentle suggestion. Nothing else, ever.
            </p>
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer gap-3">
          <span className="text-sm text-[#52443E]">{enabled ? "On" : "Off"}</span>
          <input
            type="checkbox"
            className="sr-only peer"
            checked={enabled}
            onChange={(e) => onChange({ weekly_letter_enabled: e.target.checked })}
            data-testid="settings-letter-toggle"
          />
          <span className="relative w-11 h-6 bg-[#EAE0D2] peer-checked:bg-[#5F7463] rounded-full transition-colors
                           after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:border-[#D8CBB9]
                           after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      </div>

      <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="settings-letter-timing">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Day</label>
          <select
            className="input-paper mt-2"
            value={cfg.letter_day}
            disabled={!enabled}
            onChange={(e) => onChange({ letter_day: Number(e.target.value) })}
            data-testid="settings-letter-day"
          >
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Around</label>
          <select
            className="input-paper mt-2"
            value={cfg.letter_hour}
            disabled={!enabled}
            onChange={(e) => onChange({ letter_hour: Number(e.target.value) })}
            data-testid="settings-letter-hour"
          >
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">Timezone</label>
          <select
            className="input-paper mt-2"
            value={cfg.letter_timezone}
            disabled={!enabled}
            onChange={(e) => onChange({ letter_timezone: e.target.value })}
            data-testid="settings-letter-timezone"
          >
            {!zones.includes(cfg.letter_timezone) && <option value={cfg.letter_timezone}>{cfg.letter_timezone}</option>}
            {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>
      <p className="mt-3 text-sm text-[#73635B] inline-flex items-center gap-2" data-testid="settings-letter-next">
        <Clock size={14} className="text-[#8C7A70]" />
        {enabled
          ? `Arrives ${DAYS[cfg.letter_day]}s around ${hourLabel(cfg.letter_hour)} (${cfg.letter_timezone.replace(/_/g, " ")}).`
          : "Turn it on and choose when it should arrive."}
      </p>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button className="btn-ghost" onClick={() => setOpen((o) => !o)} data-testid="settings-letter-preview-toggle">
          {open ? "Hide this week's letter" : "Read this week's letter"}
        </button>
        <button className="btn-ghost inline-flex items-center gap-2" onClick={sendNow} disabled={sending} data-testid="settings-letter-send-now">
          <Send size={14} /> {sending ? "Sending…" : "Email it to me now"}
        </button>
        {letter?.last_sent_at && (
          <span className="text-xs text-[#8C7A70]" data-testid="settings-letter-last-sent">
            Last sent {new Date(letter.last_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        )}
      </div>

      {open && letter && (
        <div className="mt-6 rounded-2xl border border-[#E5D9C8] bg-[#FFFDF9] p-6 sm:p-8 notebook-lines" data-testid="settings-letter-preview">
          <p className="text-xs uppercase tracking-[0.2em] text-[#8C7A70] font-semibold">{letter.subject}</p>
          <div className="mt-4 space-y-3">
            {letter.paragraphs.map((p, i) => (
              <p key={i} className="font-serif-display text-lg text-[#2C221E] leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-5 pl-4 border-l-2 border-[#C26D53]">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C26D53] font-semibold">One gentle suggestion</p>
            <p className="mt-1 text-[#52443E] leading-relaxed" data-testid="settings-letter-suggestion">{letter.suggestion}</p>
          </div>
        </div>
      )}
    </div>
  );
}
